import type {PopularFiltersConfigItem} from '@/config/models';
import type {
  Filter,
  InventoryFilters,
  MetaFilters,
  OptionFilterItem,
} from '@/modules/common/models/filters';
import type {TFunction} from 'i18next';

import {useTranslation} from 'react-i18next';

import {config} from '@/config';
import {PAGE_HOTELS, PAGE_OFFERS} from '@/consts';
import {useSearchParams} from '@/modules/common/hooks/useSearchParams';

type FiltersUsed = InventoryFilters | MetaFilters;

type PopularFiltersConfigItemExtended = PopularFiltersConfigItem & {
  translate?: (t: TFunction, option: OptionFilterItem) => string;
};

export type UsePopularFilterItems = Array<{
  active: boolean;
  id: string;
  disabled: boolean;
  label: string;
  filter: {
    id: FiltersUsed['id'];
    value: FiltersUsed['value'];
    setValue: FiltersUsed['setValue'];
  };
  value: string;
  type: PopularFiltersConfigItem['type'];
}>;

export const usePopularFilters = (
  filters: Array<Filter>,
  currentPage: string
): UsePopularFilterItems => {
  const {t} = useTranslation();
  const [searchParams] = useSearchParams();
  const searchedWithChildren = searchParams?.rooms?.childrenCount > 0;

  let enabledFilters: Array<PopularFiltersConfigItemExtended> = [];
  if (currentPage === PAGE_HOTELS) {
    enabledFilters = searchedWithChildren
      ? config.filters.popular.hotels.withChildren
      : config.filters.popular.hotels.default;
  } else if (currentPage === PAGE_OFFERS) {
    enabledFilters = searchedWithChildren
      ? config.filters.popular.offers.withChildren
      : config.filters.popular.offers.default;
  }

  for (const item of enabledFilters) {
    if (item.id === 'category') {
      item.translate = (translate, option) =>
        translate('category.label', {
          count: Number(option.value),
          ns: 'filters',
        });
    }
  }

  const items: UsePopularFilterItems = [];

  for (const filterToFind of enabledFilters) {
    const filterCategory = filters.find(
      (f): f is FiltersUsed => f.id === filterToFind.id
    );

    if (!filterCategory?.options || filterCategory.options.length === 0) {
      continue;
    }

    const option = filterCategory.options.find(
      (item) => item.value === filterToFind.value
    );

    if (!option) {
      continue;
    }

    const label = filterToFind?.translate
      ? filterToFind.translate(t, option)
      : option.label;

    items.push({
      id: `${filterToFind.id}_${option.value}`,
      label,
      disabled: !option.active && !option.available,
      active: option.active,
      value: option.value,
      type: filterToFind.type,
      filter: {
        value: filterCategory.value,
        id: filterCategory.id,
        setValue: filterCategory.setValue,
      },
    });
  }

  return items;
};
